import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { MaskLines, ease } from "@/components/site/Motion";
import { Btn, BrowserFrame, PhoneFrame, Label } from "@/components/site/Ui";
import { primaryCta, projects } from "@/content/site";

const [maison, vache, oran] = projects;

function HeroVisual() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const yBack = useTransform(scrollYProgress, [0, 1], ["6%", "-10%"]);
  const yPhone = useTransform(scrollYProgress, [0, 1], ["14%", "-18%"]);
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 70, damping: 18 });
  const sry = useSpring(ry, { stiffness: 70, damping: 18 });
  const onMove = (e) => {
    if (e.pointerType !== "mouse") return;
    const r = e.currentTarget.getBoundingClientRect();
    ry.set(((e.clientX - r.left) / r.width - 0.5) * 7);
    rx.set(-((e.clientY - r.top) / r.height - 0.5) * 7);
  };
  return (
    <div ref={ref} className="relative pb-6 [perspective:1600px] sm:pb-16" onPointerMove={onMove} onPointerLeave={() => { rx.set(0); ry.set(0); }}>
      <motion.div style={{ y: yBack }} className="absolute -top-10 right-0 hidden w-[58%] opacity-90 lg:block" aria-hidden="true">
        <BrowserFrame src={oran.shots.home} alt="" label={oran.name} />
      </motion.div>
      <motion.div style={{ rotateX: srx, rotateY: sry }} initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.2, ease, delay: 0.45 }} className="relative w-full lg:mt-24 lg:w-[86%]">
        <BrowserFrame src={maison.shots.home} alt="Page d'accueil du site Maison Sanaa, soins visage et corps" label={maison.domain} priority testid="hero-frame" />
      </motion.div>
      <motion.div style={{ y: yPhone }} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.9 }} className="absolute -bottom-6 right-2 w-[26%] max-w-[170px] sm:right-6 lg:right-[4%]">
        <PhoneFrame src={vache.shots.mobile} alt="Version mobile du site du restaurant Oh la Vache !" />
      </motion.div>
    </div>
  );
}

export default function Hero() {
  return (
    <section className="wrap relative pb-20 pt-28 md:pb-28 md:pt-36" data-testid="home-hero">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }} className="mb-8 flex flex-wrap items-center justify-between gap-3 border-b border-ink/15 pb-4 md:mb-12">
        <Label>Studio de design web indépendant</Label>
        <Label className="hidden sm:block">Sites · Réservation · Google</Label>
      </motion.div>
      <MaskLines as="h1" onLoad delay={0.05} stagger={0.1} className="display max-w-[1100px] text-[clamp(2.6rem,6vw,5.5rem)] leading-[1.08]"
        lines={["Votre entreprise mérite", "d'être trouvée.", <span key="l3">Et <span className="accent">choisie.</span></span>]} />
      <div className="mt-10 grid gap-12 lg:grid-cols-12 lg:gap-10">
        <div className="lg:col-span-4">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease, delay: 0.5 }}>
            <p className="lead max-w-md" data-testid="hero-subtitle">Je crée des sites sur mesure pour les commerces et les indépendants, pensés pour vous apporter plus d'appels, plus de réservations et une vraie place sur Google.</p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row lg:flex-col lg:items-start">
              <Btn to={primaryCta.to} track="cta_diagnostic" testid="hero-cta-primary" className="whitespace-nowrap">{primaryCta.label}</Btn>
              <Btn to="/realisations" variant="outline" testid="hero-cta-secondary" className="whitespace-nowrap">Voir mes réalisations</Btn>
            </div>
          </motion.div>
        </div>
        <div className="lg:col-span-8"><HeroVisual /></div>
      </div>
    </section>
  );
}
